<script setup lang="ts">
import type { ProductTileProps } from '../UI/UIProductTile.props'
import type { EditorialProductHeroProps } from './EditorialProductHero.props'
import emblaCarouselVue from 'embla-carousel-vue'

const {
  products: _prods,
  media,
  inverted,
} = defineProps<EditorialProductHeroProps>()

const { getProducts } = useShopTheLook()

const suffix = _prods
  .map(prod => (typeof prod === 'string' ? prod : prod.product.sku))
  .join('-')
const key = `editorial-product-hero-${suffix}`

const { data: productsRef } = await useAsyncData(
  key,
  async () => {
    if (typeof _prods[0] === 'string') {
      return getProducts(_prods as string[])
    }
    return _prods as ProductTileProps[]
  },
  {
    default: () => [],
  }
)

const products = unref(productsRef)

const isOneProduct = ref(products?.length === 1)
const isLessThanThreeProduct = ref(products?.length < 3)

const desktopSlidesPerView = computed(() =>
  isLessThanThreeProduct.value ? products?.length : 3
)

const tabletSlidesPerView = computed(() => {
  if (isOneProduct.value) return 1
  return isLessThanThreeProduct.value ? products?.length : 2.5
})

const mobileSlidesPerView = computed(() => (isOneProduct.value ? 1 : 1.5))

const [emblaRef, emblaApi] = emblaCarouselVue({
  active: !isOneProduct.value,
  loop: false,
  skipSnaps: true,
  breakpoints: {
    '(min-width: 768px)': { active: products?.length >= 3 },
    '(min-width: 1024px)': { active: products?.length > 3 },
  },
})

const canScrollNext = ref(false)
const canScrollPrev = ref(false)

const updateButtonStates = () => {
  if (!emblaApi?.value) return
  canScrollNext.value = emblaApi.value.canScrollNext()
  canScrollPrev.value = emblaApi.value.canScrollPrev()
}

// Setup carousel navigation
watchEffect(() => {
  if (!emblaApi?.value) return
  emblaApi.value.on('select', updateButtonStates)
  emblaApi.value.on('reInit', updateButtonStates)
  updateButtonStates()
})
</script>

<template>
  <div class="grid h-full grid-cols-12">
    <div
      class="left-side card-container relative col-span-full lg:col-span-6"
      :class="{ 'order-2': inverted }"
    >
      <div class="h-full w-full lg:col-span-6">
        <UIMedia
          v-bind="media"
          class="h-full w-full object-cover object-top"
          :aspect-ratio="media.media.type === 'video' ? '5/4' : undefined"
        />
      </div>
    </div>
    <div
      class="right-side bg-background-secondary relative col-span-full content-center lg:col-span-6"
      :class="{ 'order-1': inverted }"
    >
      <div class="grid grid-cols-12">
        <div
          class="relative hidden cursor-pointer lg:block"
          :class="{
            'hidden opacity-0':
              isLessThanThreeProduct || products?.length === 3,
            'opacity-35': !canScrollPrev,
          }"
          @click="emblaApi?.scrollPrev()"
        >
          <div class="arrow"></div>
        </div>
        <div
          class="group col-span-full lg:col-span-10 lg:col-start-2 lg:col-end-12"
          :class="{
            'lg-custom:px-20 lg-custom:py-20 px-10 py-10 md:px-28 lg:px-10 lg:py-20':
              isOneProduct,
          }"
        >
          <div
            ref="emblaRef"
            class="embla overflow-hidden"
            :style="{
              '--slides-per-view-mobile': mobileSlidesPerView,
              '--slides-per-view-tablet': tabletSlidesPerView,
              '--slides-per-view-desktop': desktopSlidesPerView,
            }"
          >
            <div class="embla__container flex cursor-grab select-none">
              <div
                v-for="(card, index) in products"
                :key="`${card.product.sku}-${index}`"
                class="embla__slide grid-borders-grey w-1/2 shrink-0"
              >
                <UIProductTile v-bind="card" class="h-full w-full" />
              </div>
            </div>
          </div>
        </div>
        <div
          class="relative hidden cursor-pointer lg:block"
          :class="{
            'hidden opacity-0':
              isLessThanThreeProduct || products?.length === 3,
            'opacity-35': !canScrollNext,
          }"
          @click="emblaApi?.scrollNext()"
        >
          <div class="arrow arrow--right"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.embla {
  --slides-per-view-mobile: 1.5;
  --slides-per-view-tablet: 2.5;
  --slides-per-view-desktop: 3;

  .embla__slide {
    width: calc(100% / var(--slides-per-view-mobile));

    @media (min-width: 768px) {
      width: calc(100% / var(--slides-per-view-tablet));
    }

    @media (min-width: 1024px) {
      width: calc(100% / var(--slides-per-view-desktop));
    }
  }
}

.arrow {
  width: 10px;
  height: 10px;
  border-top: 1px solid var(--colorTextPrimary);
  border-right: 1px solid var(--colorTextPrimary);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-135deg);

  &--right {
    transform: translate(-50%, -50%) rotate(45deg);
  }
}

.grid-borders-grey {
  border: 1px solid #d9d9d9;
  grid-gap: 1px;
  background-color: var(--colorStrokeCardDefault);
}

.grid-borders-black {
  border: 1px solid var(--colorStrokeCardHover);
  grid-gap: 1px;
  background-color: var(--colorStrokeCardHover);
}
</style>
